import { useEffect, useState, type ReactNode } from "react";
import { Download } from "lucide-react";
import type { Project } from "@proof/core";
import {
  Button,
  Control,
  JsonEditor,
  KeyValueEditor,
  SetupModes,
  Toggle,
  type SetupMode,
} from "./components";

/** Project-owned details and proofing behaviour, edited as part of the draft. */
export function ProjectSettings({
  project,
  onChange,
  onBackup,
  backupLabel,
  restore,
  canEdit,
}: {
  project: Project;
  onChange: (project: Project) => void;
  onBackup: () => void;
  backupLabel: string;
  /** Control that restores what the backup button downloads. */
  restore?: ReactNode;
  canEdit: boolean;
}) {
  const [metadataMode, setMetadataMode] = useState<SetupMode>("guided");
  // A cleared name is kept locally so it can be retyped; projects need one.
  const [name, setName] = useState(project.name);
  useEffect(() => setName(project.name), [project.name]);
  const settings = project.settings;
  const set = (patch: Partial<Project["settings"]>) =>
    onChange({ ...project, settings: { ...settings, ...patch } });
  return (
    <div className="project-settings">
      <div className="page-intro">
        <div>
          <span className="eyebrow">PROJECT SETTINGS</span>
          <h1>Project settings</h1>
          <p>
            Name and describe this project and choose how its proofs behave.
            Changes are part of the draft until you save.
          </p>
        </div>
      </div>
      <fieldset className="settings-sections" disabled={!canEdit}>
        <section className="panel padded settings-section">
          <div className="settings-section-intro">
            <h2>Basics</h2>
            <p className="muted">How this project appears to your team.</p>
          </div>
          <Control
            label="Project name"
            note={name.trim() ? undefined : "Enter a name for this project."}
          >
            <input
              value={name}
              maxLength={100}
              aria-invalid={!name.trim()}
              onChange={(e) => {
                setName(e.target.value);
                if (e.target.value.trim())
                  onChange({ ...project, name: e.target.value });
              }}
              onBlur={() => setName(project.name)}
            />
          </Control>
          <Control label="Description">
            <textarea
              rows={3}
              value={project.description}
              onChange={(e) =>
                onChange({ ...project, description: e.target.value })
              }
            />
          </Control>
          <Control
            label="Shared instructions"
            note="Shown to everyone who fills in this project's form."
          >
            <textarea
              rows={3}
              value={project.instructions}
              onChange={(e) =>
                onChange({ ...project, instructions: e.target.value })
              }
            />
          </Control>
        </section>
        <section className="panel padded settings-section">
          <div className="settings-section-intro">
            <h2>Proofing</h2>
            <p className="muted">What people see while they personalize.</p>
          </div>
          <Toggle
            label="Enable proof comments"
            value={settings.comments}
            onChange={(comments) => set({ comments })}
          />
          <Toggle
            label="Lock generated proof name"
            value={settings.lockProofName}
            onChange={(lockProofName) => set({ lockProofName })}
          />
          <Toggle
            label="Show overflow warnings"
            value={settings.overflowWarning}
            onChange={(overflowWarning) => set({ overflowWarning })}
          />
          <Control
            label="Overflow warning text"
            note="Shown when text does not fit its frame."
          >
            <input
              disabled={!settings.overflowWarning}
              value={settings.overflowMessage}
              onChange={(e) => set({ overflowMessage: e.target.value })}
            />
          </Control>
        </section>
        <section className="panel padded settings-section">
          <div className="settings-section-intro settings-section-heading">
            <div>
              <h2>PDF metadata</h2>
              <p className="muted">
                Document properties written into every generated PDF, such as
                Author or Subject.
              </p>
            </div>
            <SetupModes
              label="PDF metadata setup"
              value={metadataMode}
              modes={["guided", "json"]}
              guidedLabel="Simple"
              onChange={setMetadataMode}
            />
          </div>
          {metadataMode === "guided" ? (
            <KeyValueEditor
              label="PDF metadata"
              keyLabel="Property"
              valueLabel="Value"
              addLabel="Add property"
              value={settings.metadata}
              onChange={(metadata) => set({ metadata })}
            />
          ) : (
            <JsonEditor
              label="PDF metadata"
              field={{
                type: "object",
                additionalProperties: { type: "string" },
              }}
              value={settings.metadata}
              onSave={(v) => set({ metadata: v as Record<string, string> })}
            />
          )}
        </section>
      </fieldset>
      <section className="panel padded settings-section">
        <div className="settings-section-intro">
          <h2>Backup</h2>
          <p className="muted">Keep a copy of this work outside the studio.</p>
        </div>
        <div className="button-row">
          <Button onClick={onBackup}>
            <Download size={15} />
            {backupLabel}
          </Button>
          {restore}
        </div>
      </section>
    </div>
  );
}
